import { Card, CardContent, Box, Typography, IconButton } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { formatCurrency } from '@utils/numberFormatters';
import { useTheme } from '@mui/material/styles';
import { useResponsive } from '../../../shared/hooks/useResponsive';
import type { WorkOrderCharge } from '../../types/charges.types';

type Props = {
  charge: WorkOrderCharge;
  onDelete?: (charge: WorkOrderCharge) => void;
};

export const ChargeCard = ({ charge, onDelete }: Props) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const isDarkMode = theme.palette.mode === 'dark';

  const quantity = charge.lineQuantity || 0;
  const unitCost = charge.lineCost || 0;
  const totalCost = charge.lineTotalCost || unitCost * quantity;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(charge);
    }
  };

  return (
    <Card
      sx={{
        cursor: 'default',
        transition: 'all 0.2s ease',
        border: theme => `1px solid ${theme.palette.divider}`,
        boxShadow: 0,
        position: 'relative',
        height: isMobile ? 'auto' : 80,
        minHeight: isMobile ? 100 : 80,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {onDelete && (
        <IconButton
          onClick={handleDelete}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            color: isDarkMode ? 'rgba(244, 67, 54, 0.6)' : 'error.main',
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.08)',
              color: isDarkMode ? 'rgba(244, 67, 54, 0.8)' : 'error.dark'
            }
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}

      <CardContent
        sx={{
          p: 2,
          '&:last-child': { pb: 2 },
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 3,
            pr: onDelete ? 4 : 0,
            width: '100%'
          }}
        >
          <Box
            sx={{
              flex: '0 0 auto',
              minWidth: 80,
              overflow: 'hidden'
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                mb: !isMobile && charge.lineDescription ? 0.5 : 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={charge.chargeType || 'Charge'}
            >
              {charge.chargeType || 'Charge'}
            </Typography>
            {!isMobile && charge.lineDescription && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%'
                }}
                title={charge.lineDescription}
              >
                {charge.lineDescription}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: isMobile ? 2 : 3,
              flex: '1 1 auto',
              justifyContent: 'flex-end',
              flexWrap: 'nowrap'
            }}
          >
            {quantity > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 'auto',
                  flexShrink: 1
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {quantity}
                </Typography>
                {charge.lineUnit && (
                  <Typography variant="body2" color="text.secondary">
                    {charge.lineUnit}
                  </Typography>
                )}
              </Box>
            )}

            {unitCost > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 'auto',
                  flexShrink: 1
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(unitCost)}
                </Typography>
              </Box>
            )}

            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 'auto',
                  flexShrink: 1
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(totalCost)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
